import { COUNTRY_MAP } from "Services/constants";
import { DynamicInsuranceTabSetting, DynamicSettings } from "./DynamicSettings";
import { useSaveDynamicSettings } from "./useSaveDynamicSettings";
import { html } from "lit-html";

const CHECKBOX_ELEM_PREFIX = "dynamic-insurance-tab-checkbox-";
const INPUT_ELEM_PREFIX = "dynamic-insurance-tab-input-";

export interface Props {
	newSettings: DynamicSettings;
	setNewSettings: (settings: DynamicSettings) => void;
}

export const useInsuranceTabSettings = (props: Props) => {
	const saver = useSaveDynamicSettings({ newSettings: props.newSettings });

	const handleCheckboxChange = (e: KeyboardEvent, setting: DynamicInsuranceTabSetting) => {
		e.preventDefault();
		e.stopPropagation();

		const oldSetting = props.newSettings.DynamicInsuranceTabSettings.find(
			(newSetting) => setting.Culture.toLowerCase() === newSetting.Culture.toLowerCase()
		);
		const newInsuranceSettings = oldSetting
			? props.newSettings.DynamicInsuranceTabSettings.map((newSetting) =>
					setting.Culture.toLowerCase() === newSetting.Culture.toLowerCase()
						? { ...newSetting, IsActive: !newSetting.IsActive }
						: newSetting
				)
			: [...props.newSettings.DynamicInsuranceTabSettings, { ...setting, IsActive: true }];

		props.setNewSettings({ ...props.newSettings, DynamicInsuranceTabSettings: newInsuranceSettings });
	};

	const handleInput = (e: InputEvent, setting: DynamicInsuranceTabSetting) => {
		const newValue = (e.target as HTMLInputElement).value;
		const oldSetting = props.newSettings.DynamicInsuranceTabSettings.find(
			(newSetting) => setting.Culture.toLowerCase() === newSetting.Culture.toLowerCase()
		);
		const newInsuranceSettings = oldSetting
			? props.newSettings.DynamicInsuranceTabSettings.map((newSetting) =>
					setting.Culture.toLowerCase() === newSetting.Culture.toLowerCase()
						? { ...newSetting, Url: newValue }
						: newSetting
				)
			: [...props.newSettings.DynamicInsuranceTabSettings, { ...setting, Url: newValue }];

		props.setNewSettings({ ...props.newSettings, DynamicInsuranceTabSettings: newInsuranceSettings });
	};

	const rowTemplate = (culture: string) => {
		const setting: DynamicInsuranceTabSetting = props.newSettings.DynamicInsuranceTabSettings.find(
			(s) => s.Culture.toLowerCase() === culture.toLowerCase()
		) || { Culture: culture, IsActive: false, Url: "" };
		return html`
			<tr class="border-b border-solid border-n-blue pb-4 odd:bg-gray-100">
				<td class="px-2 py-4 align-middle">
					<span class="inline-block">
						<input
							type="checkbox"
							id=${`${CHECKBOX_ELEM_PREFIX}${culture.split("-")[1]}`}
							?checked=${setting.IsActive || false}
							@change=${(e: KeyboardEvent) => handleCheckboxChange(e, setting)}
						/>
						<label for=${`${CHECKBOX_ELEM_PREFIX}${culture.split("-")[1]}`}>
							Show Insurance Tab for ${COUNTRY_MAP.get(culture)}
						</label>
					</span>
				</td>

				<td class="px-2 py-4 align-middle">
					<label class="mb-1 block text-sm" for=${`${INPUT_ELEM_PREFIX}${setting.Culture.split("-")[1]}`}>
						Button Url
					</label>
					<input
						class="flex h-10 w-full appearance-none items-center rounded-lg border border-solid border-black px-4"
						type="text"
						id=${`${INPUT_ELEM_PREFIX}${setting.Culture.split("-")[1]}`}
						value=${setting.Url || ""}
						@input=${(e: InputEvent) => handleInput(e, setting)}
					/>
				</td>
			</tr>
		`;
	};

	const htmlTemplate = () => html`
		<section class="mt-6 max-w-[640px]">
			<table class="w-full">
				${Array.from(COUNTRY_MAP.keys()).map(rowTemplate)}
			</table>
			${saver.htmlTemplate()}
		</section>
	`;

	return { htmlTemplate };
};
